import { useState } from "react";
import {
  DollarSign,
  TrendingUp,
  Package,
  Download,
  Loader2,
  Calendar,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface RevenueData {
  total: {
    revenue: string;
    purchases: number;
  };
  byPack: Array<{
    packId: string;
    title: string;
    priceUsdc: string;
    purchaseCount: number;
    totalRevenue: string;
  }>;
  trend: Array<{
    date: string;
    purchases: number;
    revenue: string;
  }>;
}

interface Purchase {
  id: string;
  agentId: string;
  packId: string;
  priceUsdc: string;
  status: string;
  txHash: string | null;
  paidByAgent: boolean;
  createdAt: string;
  completedAt: string | null;
  agent: {
    id: string;
    name: string;
    avatarUrl: string | null;
  };
  pack: {
    packId: string;
    title: string;
  };
  paidByUser: {
    id: string;
    displayName: string;
  } | null;
}

export default function CertificationRevenue() {
  const [page, setPage] = useState(0);
  const limit = 50;

  // Fetch revenue data
  const { data: revenueResponse, isLoading: revenueLoading } = useQuery({
    queryKey: ["admin", "certification-revenue"],
    queryFn: async () => {
      const res = await api.get("/admin/certification-revenue");
      return res.data;
    },
  });

  const revenue = revenueResponse?.data as RevenueData | undefined;

  // Fetch purchases
  const { data: purchasesResponse, isLoading: purchasesLoading } = useQuery({
    queryKey: ["admin", "certification-purchases", page],
    queryFn: async () => {
      const res = await api.get(`/admin/certification-purchases?limit=${limit}&offset=${page * limit}`);
      return res.data;
    },
  });

  const purchases = purchasesResponse?.data?.purchases as Purchase[] | undefined;
  const total = purchasesResponse?.data?.total || 0;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Certification Revenue</h1>
          <p className="text-muted-foreground">Track certification sales and revenue</p>
        </div>
        <Button variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Revenue Stats */}
      {revenueLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Revenue */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                ${parseFloat(revenue?.total.revenue || "0").toLocaleString()} USDC
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                From {revenue?.total.purchases || 0} purchase{revenue?.total.purchases !== 1 ? "s" : ""}
              </p>
            </CardContent>
          </Card>

          {/* Total Purchases */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Purchases</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{revenue?.total.purchases || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">All time</p>
            </CardContent>
          </Card>

          {/* Active Packs */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Paid Packs</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {revenue?.byPack.length || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Generating revenue
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue by Pack */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue by Pack</CardTitle>
          <CardDescription>Top earning evaluation packs</CardDescription>
        </CardHeader>
        <CardContent>
          {revenueLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : revenue?.byPack.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No paid packs yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {revenue?.byPack.map((pack, index) => (
                <div
                  key={pack.packId}
                  className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="molt-icon-surface flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{pack.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {pack.packId} • ${pack.priceUsdc} USDC
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">${parseFloat(pack.totalRevenue).toLocaleString()} USDC</p>
                    <p className="text-sm text-muted-foreground">
                      {pack.purchaseCount} sale{pack.purchaseCount !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Trend */}
      {revenue?.trend && revenue.trend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Revenue Trend (Last 30 Days)</CardTitle>
            <CardDescription>Daily purchase activity</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {revenue.trend.slice(0, 7).map((day) => (
                <div
                  key={day.date}
                  className="flex items-center justify-between p-3 bg-muted/20 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {formatDate(day.date)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      {day.purchases} purchase{day.purchases !== 1 ? "s" : ""}
                    </span>
                    <span className="font-semibold">
                      ${parseFloat(day.revenue).toLocaleString()} USDC
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Purchases */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Purchases</CardTitle>
          <CardDescription>Latest certification purchases</CardDescription>
        </CardHeader>
        <CardContent>
          {purchasesLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : purchases?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No purchases yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Pack</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Paid By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchases?.map((purchase) => (
                    <TableRow key={purchase.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {purchase.agent.avatarUrl && (
                            <img
                              src={purchase.agent.avatarUrl}
                              alt={purchase.agent.name}
                              className="w-6 h-6 rounded-full"
                            />
                          )}
                          <span className="font-medium">{purchase.agent.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{purchase.pack.title}</p>
                          <p className="text-xs text-muted-foreground">{purchase.pack.packId}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">
                        ${purchase.priceUsdc} USDC
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {purchase.paidByAgent ? (
                            <Badge variant="secondary">Agent</Badge>
                          ) : (
                            <>
                              <Badge variant="outline">User</Badge>
                              <span className="text-xs text-muted-foreground">
                                {purchase.paidByUser?.displayName}
                              </span>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {purchase.status === "COMPLETED" && (
                          <Badge className="bg-emerald-500">Completed</Badge>
                        )}
                        {purchase.status === "PENDING" && (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                        {purchase.status === "REFUNDED" && (
                          <Badge variant="destructive">Refunded</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{formatDate(purchase.createdAt)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatTime(purchase.createdAt)}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={(page + 1) * limit >= total}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
